import React, { Suspense, lazy } from 'react';
import { Hero } from './components/Hero';

/**
 * CATIO READY™ sales page.
 * Pre-offer:  HERO → VALUE → REAL PAGES → PLAN → SYSTEM + TECH PROOF →
 *             5 VOLUMES → 15 PROJECTS → FREE BONUSES → VALUE STACK → OFFER
 * Post-offer: VOLUME DETAIL → OBJECTIONS → ACCESS → FAQ → INFO → FINAL CTA
 *
 * Everything below the hero loads in separate chunks so the hero (mobile LCP)
 * ships with minimal JS. Each block reserves height while loading.
 */
const named = <K extends string,>(loader: () => Promise<Record<K, React.ComponentType>>, key: K) => lazy(() => loader().then(m => ({
  default: m[key]
})));
const ValueStrip = named(() => import('./components/sections/ValueStrip'), 'ValueStrip');
const PageProofSection = named(() => import('./components/sections/PageProofSection'), 'PageProofSection');
const PlanSection = named(() => import('./components/sections/PlanSection'), 'PlanSection');
const SystemSection = named(() => import('./components/sections/SystemSection'), 'SystemSection');
const CollectionSection = named(() => import('./components/sections/CollectionSection'), 'CollectionSection');
const GallerySection = named(() => import('./components/sections/GallerySection'), 'GallerySection');
const BonusSection = named(() => import('./components/sections/BonusSection'), 'BonusSection');
const ValueStackSection = named(() => import('./components/sections/ValueStackSection'), 'ValueStackSection');
const OfferSection = named(() => import('./components/sections/OfferSection'), 'OfferSection');
const VolumeDetailSection = named(() => import('./components/sections/VolumeDetailSection'), 'VolumeDetailSection');
const ObjectionSection = named(() => import('./components/sections/ObjectionSection'), 'ObjectionSection');
const AccessSection = named(() => import('./components/sections/AccessSection'), 'AccessSection');
const FaqSection = named(() => import('./components/sections/FaqSection'), 'FaqSection');
const DisclaimerSection = named(() => import('./components/sections/DisclaimerSection'), 'DisclaimerSection');
const FinalCta = named(() => import('./components/sections/FinalCta'), 'FinalCta');
const Footer = named(() => import('./components/Footer'), 'Footer');
const StickyBuyBar = named(() => import('./components/StickyBuyBar'), 'StickyBuyBar');
interface DeferredProps {
  children: React.ReactNode;
  minHeight: string;
  background?: string;
  id?: string;
}

/** The fallback carries the section id so #offer resolves even before the chunk loads. */
function Deferred({
  children,
  minHeight,
  background = 'bg-ivory',
  id
}: DeferredProps) {
  return <Suspense fallback={<div id={id} className={`w-full ${background}`} style={{
    minHeight
  }} aria-hidden data-id="element-1" data-mp="3qbzgktko3e96" />} data-id="element-0" data-mp="3qbpql5zmh73h">
      {children}
    </Suspense>;
}
export function App() {
  return <main className="w-full overflow-x-clip bg-ivory text-ink" data-id="element-2" data-mp="2xuda1slwb3uk">
      <Hero data-id="element-3" data-mp="2xssj0cysi31g" />

      <Deferred minHeight="96px" data-id="element-4" data-mp="2svtmjnrnlk03">
        <ValueStrip data-id="element-5" data-mp="2suqydnlptvnv" />
      </Deferred>
      <Deferred minHeight="820px" background="bg-paper" data-id="element-6" data-mp="2stpm5fsyr34k">
        <PageProofSection data-id="element-7" data-mp="18dhu6akcxc96" />
      </Deferred>
      <Deferred minHeight="760px" data-id="element-8" data-mp="18bokemeg6bwy">
        <PlanSection data-id="element-9" data-mp="1d4zsequzwyqg" />
      </Deferred>
      <Deferred minHeight="1400px" background="bg-paper" data-id="element-10" data-mp="1d62i4vgl0v4s">
        <SystemSection data-id="element-11" data-mp="yg3nv3s5e3ul" />
      </Deferred>
      <Deferred minHeight="820px" background="bg-stone" data-id="element-12" data-mp="yh6ct65wtw7v">
        <CollectionSection data-id="element-13" data-mp="13e54zj60jt3f" />
      </Deferred>
      <Deferred minHeight="900px" background="bg-ink" data-id="element-14" data-mp="13f7ylyuq09n1">
        <GallerySection data-id="element-15" data-mp="1r3r5qb21agdj" />
      </Deferred>
      <Deferred minHeight="1200px" background="bg-paper" data-id="element-16" data-mp="1r4siql2m1cxw">
        <BonusSection data-id="element-17" data-mp="1w3vj3s7nauzj" />
      </Deferred>
      <Deferred minHeight="900px" background="bg-stone" data-id="element-18" data-mp="1w2u0n2oav67s">
        <ValueStackSection data-id="element-19" data-mp="1i443xo5e78jy" />
      </Deferred>

      <Deferred minHeight="1100px" background="bg-ink" id="offer" data-id="element-20" data-mp="1i3b3t60xvhas">
        <OfferSection data-id="element-21" data-mp="1m44s40iamy47" />
      </Deferred>

      <Deferred minHeight="560px" background="bg-paper" data-id="element-22" data-mp="1m791rdej2m29">
        <VolumeDetailSection data-id="element-23" data-mp="6ncmkevlxoyh" />
      </Deferred>
      <Deferred minHeight="760px" data-id="element-24" data-mp="6qs5jsseji56">
        <ObjectionSection data-id="element-25" data-mp="aq9bsofnkjbe" />
      </Deferred>
      <Deferred minHeight="420px" background="bg-stone" data-id="element-26" data-mp="arc3gyksupsd">
        <AccessSection data-id="element-27" data-mp="qxf6blzmsvg6" />
      </Deferred>
      <Deferred minHeight="760px" data-id="element-28" data-mp="qygkhzby1y24">
        <FaqSection data-id="element-29" data-mp="m6p3ogilb6p6" />
      </Deferred>
      <Deferred minHeight="280px" data-id="element-30" data-mp="m5nfrbgh6pq1">
        <DisclaimerSection data-id="element-31" data-mp="m4cf3kibty35" />
      </Deferred>
      <Deferred minHeight="620px" background="bg-paper" data-id="element-32" data-mp="h6kmspdrs9yf">
        <FinalCta data-id="element-33" data-mp="h4zocooc46va" />
      </Deferred>
      <Deferred minHeight="260px" background="bg-ink" data-id="element-34" data-mp="c90uwmo5fyo0">
        <Footer data-id="element-35" data-mp="c66dz54cgvzc" />
      </Deferred>

      <Suspense fallback={null} data-id="element-36" data-mp="19wjdep7m4f57">
        <StickyBuyBar data-id="element-37" data-mp="19uopr34yi8xf" />
      </Suspense>
    </main>;
}